import { EpisodeTreatmentProgram } from './patients.constants';

export enum AppointmentTypeName {
  ADMISSION_CONSULTATION = 'Admissions Consultation',
  THERAPY_INTAKE = 'Therapy Intake',
  THERAPY_SESSION = 'Therapy Session',
  PSYCHIATRIC_INTAKE = 'Psychiatric Intake',
  MEDICATION_MANAGEMENT_SESSION = 'Medication Management Session',
  MEDICAL_PROVIDER_INTAKE = 'Medical Provider Intake',
  MEDICAL_PROVIDER_SESSION = 'Medical Provider Session',
  REGISTERED_NURSE_SESSION = 'Registered Nurse Session',
  MEDICAL_ASSISTANT_SESSION = 'Medical Assistant Session',
  PEER_MENTOR_INTAKE = 'Peer Mentor Intake',
  PEER_MENTOR_SESSION = 'Peer Mentor Session',
  RECOVERY_COACH_INTAKE = 'Recovery Coach Intake',
  FAMILY_MENTOR_INTAKE = 'Family Mentor Intake',
  FAMILY_MENTOR_SESSION = 'Family Mentor Session',
  RECOVERY_COACH_SESSION = 'Recovery Coach Session',
  CAREGIVER_COACH_INTAKE = 'Caregiver Coach Intake',
  CAREGIVER_COACH_SESSION = 'Caregiver Coach Session',
  CARE_NAVIGATOR_INTAKE = 'Care Navigator Intake',
  CARE_NAVIGATOR_SESSION = 'Care Navigator Session',
  DIETITIAN_INTAKE = 'Dietitian Intake',
  DIETITIAN_SESSION = 'Dietitian Session',
  RECOVERY_NAVIGATOR_SESSION = 'Recovery Navigator Session',
  MEAL_SUPPORT_SESSION = 'Meal Support Session',
  MEDICAL_INTAKE = 'Medical Intake',
  MEDICAL_SESSION = 'Medical Session',
  TREATMENT_TEAM_HUDDLE = 'Treatment Team Huddle',
  THERAPY_DISCHARGE_SESSION = 'Therapy Discharge Session',
}

export const QUALIFYING_CALL = 'Qualifying call';

export const INTAKE_SESSION_APPOINTMENT_TYPE_NAMES = [
  AppointmentTypeName.DIETITIAN_INTAKE,
  AppointmentTypeName.FAMILY_MENTOR_INTAKE,
  AppointmentTypeName.CAREGIVER_COACH_INTAKE,
  AppointmentTypeName.MEDICAL_PROVIDER_INTAKE,
  AppointmentTypeName.MEDICAL_INTAKE,
  AppointmentTypeName.PEER_MENTOR_INTAKE,
  AppointmentTypeName.RECOVERY_COACH_INTAKE,
  AppointmentTypeName.CARE_NAVIGATOR_INTAKE,
  AppointmentTypeName.PSYCHIATRIC_INTAKE,
  QUALIFYING_CALL,
  AppointmentTypeName.THERAPY_INTAKE,
];

export const TREATMENT_SESSION_APPOINTMENT_TYPE_NAMES = [
  AppointmentTypeName.DIETITIAN_SESSION,
  AppointmentTypeName.FAMILY_MENTOR_SESSION,
  AppointmentTypeName.CAREGIVER_COACH_SESSION,
  AppointmentTypeName.MEDICAL_ASSISTANT_SESSION,
  AppointmentTypeName.MEDICAL_PROVIDER_SESSION,
  AppointmentTypeName.MEDICAL_SESSION,
  AppointmentTypeName.MEDICATION_MANAGEMENT_SESSION,
  AppointmentTypeName.MEAL_SUPPORT_SESSION,
  AppointmentTypeName.PEER_MENTOR_SESSION,
  AppointmentTypeName.RECOVERY_COACH_SESSION,
  AppointmentTypeName.CARE_NAVIGATOR_SESSION,
  AppointmentTypeName.REGISTERED_NURSE_SESSION,
  AppointmentTypeName.THERAPY_SESSION,
];

export const THERAPY_DISCHARGE_SESSION_APPOINTMENT_TYPE_NAMES = [
  AppointmentTypeName.THERAPY_DISCHARGE_SESSION,
];

export const CARE_NAVIGATION_APPOINTMENT_TYPE_NAMES = [
  AppointmentTypeName.RECOVERY_NAVIGATOR_SESSION,
];

export const TREATMENT_TEAM_HUDDLE_APPOINTMENT_TYPE_NAMES = [
  AppointmentTypeName.TREATMENT_TEAM_HUDDLE,
];

export enum AllowedParticipants {
  ALL_PROXY_SUPPORTS,
  ALL_SUPPORTS,
  NONE,
  PATIENT,
  PATIENT_AND_PROXY_SUPPORTS,
}

export const participantsOptionMapper: Record<
  AllowedParticipants,
  DefaultAllowedParticipants
> = {
  [AllowedParticipants.ALL_PROXY_SUPPORTS]: {
    generalSupport: false,
    patient: false,
    proxySupport: true,
  },
  [AllowedParticipants.ALL_SUPPORTS]: {
    generalSupport: true,
    patient: false,
    proxySupport: true,
  },
  [AllowedParticipants.NONE]: {
    generalSupport: false,
    patient: false,
    proxySupport: false,
  },
  [AllowedParticipants.PATIENT]: {
    generalSupport: false,
    patient: true,
    proxySupport: false,
  },
  [AllowedParticipants.PATIENT_AND_PROXY_SUPPORTS]: {
    generalSupport: false,
    patient: true,
    proxySupport: true,
  },
};

const NO_PARTICIPANTS = Object.fromEntries(
  Object.values(AppointmentTypeName).map(
    (apptTypeName: AppointmentTypeName) => [
      apptTypeName,
      participantsOptionMapper[AllowedParticipants.NONE],
    ],
  ),
) as Record<AppointmentTypeName, DefaultAllowedParticipants>;

const PATIENT_AND_PROXY_SUPPORT_APPT_TYPES: AppointmentTypeName[] = [
  AppointmentTypeName.THERAPY_INTAKE,
  AppointmentTypeName.THERAPY_SESSION,
  AppointmentTypeName.PSYCHIATRIC_INTAKE,
  AppointmentTypeName.MEDICATION_MANAGEMENT_SESSION,
  AppointmentTypeName.MEDICAL_PROVIDER_INTAKE,
  AppointmentTypeName.MEDICAL_PROVIDER_SESSION,
  AppointmentTypeName.REGISTERED_NURSE_SESSION,
  AppointmentTypeName.MEDICAL_ASSISTANT_SESSION,
  AppointmentTypeName.DIETITIAN_INTAKE,
  AppointmentTypeName.DIETITIAN_SESSION,
  AppointmentTypeName.MEDICAL_INTAKE,
  AppointmentTypeName.MEDICAL_SESSION,
  AppointmentTypeName.THERAPY_DISCHARGE_SESSION,
];

const PATIENT_ONLY_APPT_TYPES_EQUIP: AppointmentTypeName[] = [
  AppointmentTypeName.PEER_MENTOR_INTAKE,
  AppointmentTypeName.PEER_MENTOR_SESSION,
  AppointmentTypeName.RECOVERY_COACH_INTAKE,
  AppointmentTypeName.RECOVERY_COACH_SESSION,
  AppointmentTypeName.CARE_NAVIGATOR_INTAKE,
  AppointmentTypeName.CARE_NAVIGATOR_SESSION,
  AppointmentTypeName.MEAL_SUPPORT_SESSION,
];

const ALL_PROXY_SUPPORT_APPT_TYPES_EQUIP: AppointmentTypeName[] = [
  AppointmentTypeName.FAMILY_MENTOR_INTAKE,
  AppointmentTypeName.FAMILY_MENTOR_SESSION,
  AppointmentTypeName.CAREGIVER_COACH_INTAKE,
  AppointmentTypeName.CAREGIVER_COACH_SESSION,
  AppointmentTypeName.RECOVERY_NAVIGATOR_SESSION,
];

const PATIENT_ONLY_APPT_TYPES_ADULT: AppointmentTypeName[] = [
  AppointmentTypeName.THERAPY_INTAKE,
  AppointmentTypeName.THERAPY_SESSION,
  AppointmentTypeName.PSYCHIATRIC_INTAKE,
  AppointmentTypeName.MEDICATION_MANAGEMENT_SESSION,
  AppointmentTypeName.MEDICAL_PROVIDER_INTAKE,
  AppointmentTypeName.MEDICAL_PROVIDER_SESSION,
  AppointmentTypeName.REGISTERED_NURSE_SESSION,
  AppointmentTypeName.MEDICAL_ASSISTANT_SESSION,
  AppointmentTypeName.PEER_MENTOR_INTAKE,
  AppointmentTypeName.PEER_MENTOR_SESSION,
  AppointmentTypeName.RECOVERY_COACH_INTAKE,
  AppointmentTypeName.RECOVERY_COACH_SESSION,
  AppointmentTypeName.CARE_NAVIGATOR_INTAKE,
  AppointmentTypeName.CARE_NAVIGATOR_SESSION,
  AppointmentTypeName.DIETITIAN_INTAKE,
  AppointmentTypeName.DIETITIAN_SESSION,
  AppointmentTypeName.RECOVERY_NAVIGATOR_SESSION,
  AppointmentTypeName.MEAL_SUPPORT_SESSION,
  AppointmentTypeName.MEDICAL_INTAKE,
  AppointmentTypeName.MEDICAL_SESSION,
  AppointmentTypeName.THERAPY_DISCHARGE_SESSION,
];

function buildParticipantsMapping(
  rules: Array<{ types: AppointmentTypeName[]; allowed: AllowedParticipants }>,
): Record<AppointmentTypeName, DefaultAllowedParticipants> {
  const mapping = {} as Record<AppointmentTypeName, DefaultAllowedParticipants>;
  rules.forEach(({ types, allowed }) => {
    types.forEach((t) => {
      mapping[t] = participantsOptionMapper[allowed];
    });
  });
  Object.values(AppointmentTypeName).forEach((t) => {
    if (!mapping[t]) {
      mapping[t] = participantsOptionMapper[AllowedParticipants.NONE];
    }
  });
  return mapping;
}

const EquipAdultTreatmentParticipants: Record<
  AppointmentTypeName,
  DefaultAllowedParticipants
> = buildParticipantsMapping([
  {
    types: PATIENT_ONLY_APPT_TYPES_ADULT,
    allowed: AllowedParticipants.PATIENT,
  },
  {
    types: [
      AppointmentTypeName.FAMILY_MENTOR_INTAKE,
      AppointmentTypeName.CAREGIVER_COACH_INTAKE,
    ],
    allowed: AllowedParticipants.NONE,
  },
  {
    types: [
      AppointmentTypeName.FAMILY_MENTOR_SESSION,
      AppointmentTypeName.CAREGIVER_COACH_SESSION,
    ],
    allowed: AllowedParticipants.ALL_SUPPORTS,
  },
]);

const defaultParticipantsMapping = buildParticipantsMapping([
  {
    types: PATIENT_AND_PROXY_SUPPORT_APPT_TYPES,
    allowed: AllowedParticipants.PATIENT_AND_PROXY_SUPPORTS,
  },
  {
    types: PATIENT_ONLY_APPT_TYPES_EQUIP,
    allowed: AllowedParticipants.PATIENT,
  },
  {
    types: ALL_PROXY_SUPPORT_APPT_TYPES_EQUIP,
    allowed: AllowedParticipants.ALL_PROXY_SUPPORTS,
  },
]);

export const defaultAllowedParticipantsMapper: Record<
  EpisodeTreatmentProgram,
  Record<AppointmentTypeName, DefaultAllowedParticipants>
> = {
  [EpisodeTreatmentProgram.EQUIP]: defaultParticipantsMapping,
  [EpisodeTreatmentProgram.EQUIP_ADULT]: EquipAdultTreatmentParticipants,
  [EpisodeTreatmentProgram.FREEFORM]: NO_PARTICIPANTS,
  [EpisodeTreatmentProgram.RELAPSE_PREVENTION]: NO_PARTICIPANTS,
  [EpisodeTreatmentProgram.OPEN_CARE_MODEL]: EquipAdultTreatmentParticipants,
  [EpisodeTreatmentProgram.TRANSITION]: defaultParticipantsMapping,
};

//Cronofy maximum end date limit is 201 days,
//but may because of timezones and day light saving checking on 201st day may get issue, so on safe side using 199 days
//https://docs.cronofy.com/developers/api/scheduling/availability/
export const CONFLICTS_RECURRENCE_CHECK_DAYS = 199;

export const MEDICAL_ASSISTANT_SESSION_DEFAULT_DURATION = 15;

export const SCHEDULE_CONFLICT_TEXT =
  'Please note, no event will be created on dates with a conflict. The following dates have conflicts:';

export enum GroupClassEventOption {
  ADD_HOST,
  ADD_NOTE,
  CANCEL,
  DECLINE,
  VIEW_CLASS_INFO,
}

export enum AppointmentEventOption {
  ADD_NOTE,
  CANCEL,
  EDIT,
}

export enum AppointmentAttendeesAllowed {
  PROVIDER_ONLY = 'PROVIDER_ONLY',
  ALL = 'ALL',
}

/**
 * Treatment program codes that match backend enum constants.
 *
 * Values align with com.equip.schedule.infra.http.dto.TreatmentProgram enum.
 * For display names, use a separate mapping or the EpisodeTreatmentProgram enum
 * from patients.constants.ts.
 */
export enum TreatmentProgramCode {
  EQUIP_TREATMENT = 'EQUIP_TREATMENT',
  FREEFORM = 'FREEFORM',
  RELAPSE_PREVENTION = 'RELAPSE_PREVENTION',
  EQUIP_ADULT = 'EQUIP_ADULT',
  OPEN_CARE_MODEL = 'OPEN_CARE_MODEL',
  TRANSITION = 'TRANSITION_TRACK',
}
